import { gql } from '@apollo/client';
import { BackfillInvokeResult } from './group-performance';

/**
 * GraphQL operations for the super-admin data-export control surface.
 *
 * All mutations are gated server-side via `requireSuperAdmin` in
 * `view-api/.../DataExportAdmin.scala`. The page that consumes them
 * (`components/pages/data-export`) is additionally gated by `isSuperAdmin`
 * in the sidebar so the route never appears for non-admins.
 *
 * Merged-batch model: the BATCH is the consumer-facing unit — one merged
 * artifact per batch, deliveries attach to the batch, and the per-batch
 * download URL signs the merged artifact. Runs (window parts) are
 * admin-only production diagnostics with no actions.
 *
 * Every mutation takes `organizationId: Long!` as its first argument; the
 * server asserts `organizationId === routedOrgId` and rejects mismatches.
 * That guards against a super-admin's stale FE state (e.g. mid-route-change)
 * dispatching against the wrong org.
 *
 * `dryRun` exists only on the trigger mutation — it short-circuits the
 * coordinator before any DB writes or fanout. The redeliver mutations have
 * no dry-run mode; their failures arrive as GraphQL errors (the result type
 * has no `success` flag).
 *
 * `runId` on `BackfillInvokeResult` is a server-generated UUID (per
 * coordinator invocation) round-tripped to the coordinator so operators can
 * correlate the GraphQL response with the audit table directly.
 */

const RESULT_FRAGMENT = `
    success
    runId
    message
`;

/* ===== run + batch + delivery types ===== */

export type DataExportRunStatus = 'pending' | 'running' | 'producing' | 'success' | 'failed';

// 'manual' and 'scheduled' are the values new writers use post the
// unified-trigger redesign. 'schedule' / 'backfill' / 'redeliver' /
// 'on_demand' are kept in the union so the FE can both display
// historical rows and filter them in the batch-history queries.
export type DataExportTriggerType = 'manual' | 'scheduled' | 'schedule' | 'backfill' | 'redeliver' | 'on_demand';

/**
 * One `~~export_run` row — a single produce cycle for one window part.
 * Pure production DIAGNOSTICS under the merged-batch model: deliveries
 * attach to the batch (the merged artifact is the only thing ever
 * delivered), so runs carry no delivery state and no operator actions.
 */
export interface DataExportRun {
  id: number;
  batchId: number;
  shapeId: string;
  configName: string;
  windowStart: string;
  windowEnd: string;
  status: DataExportRunStatus;
  triggeredBy: DataExportTriggerType;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  errorPhase: string | null;
  /** Rows in this window's staged part. Null until production succeeds. */
  rowCount: number | null;
  /** Bytes in this window's staged part. Null until production succeeds. */
  byteCount: number | null;
  createdAt: string;
}

/**
 * Production lifecycle as presented by the API. The server collapses
 * its internal stages at the boundary (`merging` → `running`,
 * `partial` → `failed`) — operators only ever see these four values.
 * `failed` is terminal until `retryDataExportBatch`. Delivery outcomes
 * are NOT rolled in — a `success` batch can still have failed deliveries
 * (see `hasFailedDeliveries`).
 */
export type DataExportBatchStatus = 'pending' | 'running' | 'success' | 'failed';

export interface DataExportBatch {
  id: number;
  shapeId: string;
  configName: string;
  rangeStart: string;
  rangeEnd: string;
  totalWindows: number;
  status: DataExportBatchStatus;
  triggeredBy: DataExportTriggerType;
  /**
   * Staging URI of the MERGED batch artifact (the one file consumers
   * receive). Null until the merge succeeds. The FE gates the download
   * icon on non-null and then calls `dataExportBatchDownloadUrl` to sign
   * the actual URL on click.
   */
  artifactLocation: string | null;
  /** Total rows in the merged artifact. Null until the merge succeeds. */
  rowCount: number | null;
  /** Size of the merged artifact in bytes. Null until the merge succeeds. */
  byteCount: number | null;
  /**
   * Merge-phase failure cause when the batch `failed` during the merge.
   * Production failures live on the run rows (`errorMessage`/`errorPhase`).
   */
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  /**
   * True when this batch has at least one `~~export_delivery` row. False
   * for the operator-download / stage-only path (trigger fired with no
   * channels). Gates the batch-level Redeliver button.
   */
  hasDeliveries: boolean;
  /**
   * True when at least one of this batch's delivery rows is `failed`.
   * The batch's own `status` reflects production+merge only — a batch
   * can be `success` while a delivery failed (e.g. customer bucket
   * unreachable).
   */
  hasFailedDeliveries: boolean;
  /**
   * Window-part runs in this batch, oldest-first by `created_at`
   * (chronological window order). Admin-only diagnostics — no actions.
   */
  runs: DataExportRun[];
}

export type DataExportDeliveryStatus = 'pending' | 'delivering' | 'success' | 'failed';
export type DataExportDeliveryKind = 'initial' | 'redelivery';

/**
 * One `~~export_delivery` row — a single attempt to push the BATCH's
 * merged artifact to one channel.
 */
export interface DataExportDelivery {
  id: number;
  batchId: number;
  deliveryChannelId: number;
  /**
   * `{name, type, ...config}` blob captured at this row's insert time —
   * raw JSON string. The page parses lazily for display only.
   */
  channelSnapshotJson: string;
  status: DataExportDeliveryStatus;
  kind: DataExportDeliveryKind;
  /**
   * Redelivery rows only: when the dispatching component pre-stamped the
   * row before invoking AWS. Always null on `initial` rows.
   */
  dispatchedAt: string | null;
  deliveredLocation: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
}

/**
 * Outcome of the two redeliver mutations. No `success` flag — a resolved
 * mutation means the rows were inserted (failures arrive as GraphQL
 * errors). `skippedChannels` names voided channels that were silently
 * skipped (batch scope only — the per-delivery scope rejects loudly).
 */
export type RedeliverResult = {
  newDeliveryIds: number[];
  skippedChannels: string[];
  message: string;
};

/* ===== channel + definition + schedule types ===== */

export interface DataExportDeliveryChannel {
  id: number;
  name: string;
  type: string; // server returns the discriminator; narrow at use sites
  /** Raw JSON config blob: {"bucket": "..."} for s3, {"directory": "..."} for local. */
  configJson: string;
  createdAt: string;
  voidedAt: string | null;
}

/**
 * Where a schedule's covered range ends: 'today' includes the partial
 * current day at firing time; 'yesterday' covers complete days only.
 */
export type DataExportScheduleRangeEndAnchor = 'today' | 'yesterday';

export interface DataExportSchedule {
  id: number;
  firingHour: number;
  /**
   * Range width in days covered by each firing, ending at
   * `rangeEndAnchor`. Coverage is a schedule concern — the definition's
   * windowDays is production chunking only.
   */
  lookbackDays: number;
  rangeEndAnchor: DataExportScheduleRangeEndAnchor;
  /**
   * `~~export_delivery_channel` ids currently attached to this schedule
   * (voided channels excluded server-side). Names/types are hydrated
   * client-side from the separate `dataExportDeliveryChannels` query so
   * the schedule payload stays compact and a channel rename does not
   * have to invalidate every schedule's cache entry.
   */
  attachedChannelIds: number[];
}

/**
 * Compact health diagnostic for a definition's most recent scheduled
 * batch. Batch-level on purpose: scheduled firings produce multiple
 * window runs, and the newest run can be success while a sibling
 * window failed — the batch status aggregates all windows. `status`
 * is the presented vocabulary; `hasFailedDeliveries` covers the other
 * failure axis (produced fine, but a channel push failed — fix via
 * Redeliver in the history table).
 */
export interface DataExportLastScheduledBatch {
  id: number;
  rangeStart: string;
  rangeEnd: string;
  status: DataExportBatchStatus;
  errorMessage: string | null;
  hasFailedDeliveries: boolean;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface DataExportDefinition {
  id: number;
  shapeId: string;
  name: string;
  windowDays: number;
  outputTimezone: string;
  outputFormat: string | null;
  schedules: DataExportSchedule[];
  /**
   * The most recent scheduled-trigger batch for this (shape, name),
   * regardless of status. `null` means it has never fired on a
   * schedule yet.
   */
  lastScheduledBatch: DataExportLastScheduledBatch | null;
}

/* ===== fragments ===== */

const RUN_FRAGMENT = `
    id
    batchId
    shapeId
    configName
    windowStart
    windowEnd
    status
    triggeredBy
    startedAt
    completedAt
    errorMessage
    errorPhase
    rowCount
    byteCount
    createdAt
`;

const BATCH_FRAGMENT = `
    id
    shapeId
    configName
    rangeStart
    rangeEnd
    totalWindows
    status
    triggeredBy
    artifactLocation
    rowCount
    byteCount
    errorMessage
    startedAt
    completedAt
    createdAt
    hasDeliveries
    hasFailedDeliveries
    runs {${RUN_FRAGMENT}}
`;

const DELIVERY_FRAGMENT = `
    id
    batchId
    deliveryChannelId
    channelSnapshotJson
    status
    kind
    dispatchedAt
    deliveredLocation
    startedAt
    completedAt
    errorMessage
    createdAt
`;

const CHANNEL_FRAGMENT = `
    id
    name
    type
    configJson
    createdAt
    voidedAt
`;

const REDELIVER_RESULT_FRAGMENT = `
    newDeliveryIds
    skippedChannels
    message
`;

/* ===== queries ===== */

/**
 * Reads the current org's `~~export_definition` rows + their currently-active
 * `~~export_schedule` firing hours. Each definition also carries its most
 * recent scheduled-trigger batch (`lastScheduledBatch`).
 *
 * `organizationId` is passed as a query variable purely so Apollo keys
 * the cache per org. Without it, Apollo stores one cache entry keyed
 * `(DataExportDefinitions, {})` and navigating between
 * `/su/<orgId>/data-export` for different orgs renders the previous
 * org's definitions from cache while the new fetch is in flight, letting
 * a fast submit dispatch wrong-org values. The server additionally
 * asserts `organizationId === routedOrgId` and rejects mismatches.
 */
export const DATA_EXPORT_DEFINITIONS = gql`
  query DataExportDefinitions($organizationId: Long!) {
    dataExportDefinitions(organizationId: $organizationId) {
      id
      shapeId
      name
      windowDays
      outputTimezone
      outputFormat
      schedules {
        id
        firingHour
        lookbackDays
        rangeEndAnchor
        attachedChannelIds
      }
      lastScheduledBatch {
        id
        rangeStart
        rangeEnd
        status
        errorMessage
        hasFailedDeliveries
        startedAt
        completedAt
        createdAt
      }
    }
  }
`;

export interface DataExportDefinitionsData {
  dataExportDefinitions: DataExportDefinition[];
}

export interface DataExportDefinitionsVars {
  organizationId: number;
}

/**
 * Reads the current org's active `~~export_delivery_channel` rows.
 * Voided channels are excluded server-side. Used by the Channels tab list
 * and by the channel pickers in the Manual trigger panel + Scheduled
 * attach/detach UI.
 */
export const DATA_EXPORT_DELIVERY_CHANNELS = gql`
  query DataExportDeliveryChannels($organizationId: Long!) {
    dataExportDeliveryChannels(organizationId: $organizationId) {${CHANNEL_FRAGMENT}}
  }
`;

export interface DataExportDeliveryChannelsData {
  dataExportDeliveryChannels: DataExportDeliveryChannel[];
}

export interface DataExportDeliveryChannelsVars {
  organizationId: number;
}

export interface DataExportBatchPage {
  batches: DataExportBatch[];
  totalCount: number;
}

/**
 * Paginated history of `~~export_batch` rows for the current org, filtered
 * by trigger type, with runs eager-loaded under each batch. Both tabs use
 * this query with different `triggeredBy` filters: `["manual"]` for the
 * Manual tab, `["scheduled", "schedule"]` for the Scheduled tab. Ordered
 * newest-first by batch `created_at`. Pagination counts BATCHES.
 *
 * `pageSize` is hardcoded to 10 by the UI; the server bounds it (1..100)
 * defensively. `page` is 0-based.
 */
export const DATA_EXPORT_BATCHES = gql`
  query DataExportBatches(
    $organizationId: Long!
    $triggeredBy: [String!]!
    $page: Int!
    $pageSize: Int!
  ) {
    dataExportBatches(
      organizationId: $organizationId
      triggeredBy: $triggeredBy
      page: $page
      pageSize: $pageSize
    ) {
      batches {${BATCH_FRAGMENT}}
      totalCount
    }
  }
`;

export interface DataExportBatchesData {
  dataExportBatches: DataExportBatchPage;
}

export interface DataExportBatchesVars {
  organizationId: number;
  triggeredBy: DataExportTriggerType[];
  page: number;
  pageSize: number;
}

/**
 * Reads the `~~export_delivery` rows for one batch — initial + every
 * redelivery attempt, oldest-first. Fired lazily when the operator
 * expands a batch row in the history table.
 */
export const DATA_EXPORT_BATCH_DELIVERIES = gql`
  query DataExportBatchDeliveries($organizationId: Long!, $batchId: Long!) {
    dataExportBatchDeliveries(organizationId: $organizationId, batchId: $batchId) {${DELIVERY_FRAGMENT}}
  }
`;

export interface DataExportBatchDeliveriesData {
  dataExportBatchDeliveries: DataExportDelivery[];
}

export interface DataExportBatchDeliveriesVars {
  organizationId: number;
  batchId: number;
}

/**
 * Signs a short-lived (60s TTL) S3 GET URL for the batch's MERGED
 * artifact. Returns `null` when the batch has no merged artifact yet
 * (not found, production still running, merge pending/failed). Fired
 * lazily on download-click so URLs are only signed when actually used
 * (a row-mount would otherwise sign the URL for every visible row on
 * every poll). Super-admin only.
 */
export const DATA_EXPORT_BATCH_DOWNLOAD_URL = gql`
  query DataExportBatchDownloadUrl($organizationId: Long!, $batchId: Long!) {
    dataExportBatchDownloadUrl(organizationId: $organizationId, batchId: $batchId)
  }
`;

export interface DataExportBatchDownloadUrlData {
  dataExportBatchDownloadUrl: string | null;
}

export interface DataExportBatchDownloadUrlVars {
  organizationId: number;
  batchId: number;
}

/* ===== mutations: channels ===== */

export const CREATE_DATA_EXPORT_CHANNEL = gql`
  mutation CreateDataExportChannel(
    $organizationId: Long!
    $name: String!
    $type: String!
    $configJson: String!
  ) {
    createDataExportChannel(
      organizationId: $organizationId
      name: $name
      type: $type
      configJson: $configJson
    ) {${CHANNEL_FRAGMENT}}
  }
`;

export interface CreateDataExportChannelData {
  createDataExportChannel: DataExportDeliveryChannel;
}

export interface CreateDataExportChannelVars {
  organizationId: number;
  name: string;
  type: string;
  configJson: string;
}

export const VOID_DATA_EXPORT_CHANNEL = gql`
  mutation VoidDataExportChannel($organizationId: Long!, $channelId: Long!) {
    voidDataExportChannel(organizationId: $organizationId, channelId: $channelId)
  }
`;

export interface VoidDataExportChannelData {
  voidDataExportChannel: boolean;
}

export interface VoidDataExportChannelVars {
  organizationId: number;
  channelId: number;
}

/* ===== mutations: schedule-channel attach/detach ===== */

export const ATTACH_DATA_EXPORT_SCHEDULE_CHANNEL = gql`
  mutation AttachDataExportScheduleChannel($organizationId: Long!, $scheduleId: Long!, $channelId: Long!) {
    attachDataExportScheduleChannel(organizationId: $organizationId, scheduleId: $scheduleId, channelId: $channelId)
  }
`;

export interface AttachDataExportScheduleChannelData {
  attachDataExportScheduleChannel: boolean;
}

export interface AttachDataExportScheduleChannelVars {
  organizationId: number;
  scheduleId: number;
  channelId: number;
}

export const DETACH_DATA_EXPORT_SCHEDULE_CHANNEL = gql`
  mutation DetachDataExportScheduleChannel($organizationId: Long!, $scheduleId: Long!, $channelId: Long!) {
    detachDataExportScheduleChannel(organizationId: $organizationId, scheduleId: $scheduleId, channelId: $channelId)
  }
`;

export interface DetachDataExportScheduleChannelData {
  detachDataExportScheduleChannel: boolean;
}

export interface DetachDataExportScheduleChannelVars {
  organizationId: number;
  scheduleId: number;
  channelId: number;
}

/* ===== mutations: schedule create / void ===== */

/**
 * Creates a new active firing hour for a definition and attaches one or
 * more delivery channels atomically. `channelIds` must be non-empty —
 * the server rejects empty arrays because a schedule with zero attached
 * channels is dispatch-time-skipped.
 *
 * Returns the freshly-created schedule shape so the Apollo cache can
 * merge it into the definition tree, but the page also refetches
 * `dataExportDefinitions` on success so `lastScheduledBatch` and any
 * server-side ordering stay authoritative.
 */
export const CREATE_DATA_EXPORT_SCHEDULE = gql`
  mutation CreateDataExportSchedule(
    $organizationId: Long!
    $definitionId: Long!
    $firingHour: Int!
    $lookbackDays: Int!
    $rangeEndAnchor: String!
    $channelIds: [Long!]!
  ) {
    createDataExportSchedule(
      organizationId: $organizationId
      definitionId: $definitionId
      firingHour: $firingHour
      lookbackDays: $lookbackDays
      rangeEndAnchor: $rangeEndAnchor
      channelIds: $channelIds
    ) {
      id
      firingHour
      lookbackDays
      rangeEndAnchor
      attachedChannelIds
    }
  }
`;

export interface CreateDataExportScheduleData {
  createDataExportSchedule: DataExportSchedule;
}

export interface CreateDataExportScheduleVars {
  organizationId: number;
  definitionId: number;
  firingHour: number;
  lookbackDays: number;
  rangeEndAnchor: DataExportScheduleRangeEndAnchor;
  channelIds: number[];
}

/**
 * Soft-voids a schedule. The server rebuilds the identity-side
 * `metadata.exports.scheduledHours` cache in the same transaction so
 * the next hourly coordinator tick does not pick up the just-voided
 * hour. Returns false when the schedule does not exist or was already
 * voided — the FE surfaces that as a no-op toast rather than an error.
 */
export const VOID_DATA_EXPORT_SCHEDULE = gql`
  mutation VoidDataExportSchedule($organizationId: Long!, $scheduleId: Long!) {
    voidDataExportSchedule(organizationId: $organizationId, scheduleId: $scheduleId)
  }
`;

export interface VoidDataExportScheduleData {
  voidDataExportSchedule: boolean;
}

export interface VoidDataExportScheduleVars {
  organizationId: number;
  scheduleId: number;
}

/* ===== mutations: trigger + redelivery + retry ===== */

export const TRIGGER_DATA_EXPORT = gql`
  mutation TriggerDataExport(
    $organizationId: Long!
    $shapeId: String!
    $configName: String!
    $rangeStart: String!
    $rangeEnd: String!
    $channelIds: [Long!]!
    $dryRun: Boolean!
  ) {
    triggerDataExport(
      organizationId: $organizationId
      shapeId: $shapeId
      configName: $configName
      rangeStart: $rangeStart
      rangeEnd: $rangeEnd
      channelIds: $channelIds
      dryRun: $dryRun
    ) {${RESULT_FRAGMENT}}
  }
`;

export interface TriggerDataExportData {
  triggerDataExport: BackfillInvokeResult;
}

export interface TriggerDataExportVars {
  organizationId: number;
  shapeId: string;
  configName: string;
  rangeStart: string;
  rangeEnd: string;
  channelIds: number[];
  dryRun: boolean;
}

/**
 * Re-push one specific (batch, channel) attempt. Rejects with a GraphQL
 * error when the source delivery is not terminal, its channel was voided,
 * or the batch has no merged artifact — the FE catch path surfaces these.
 */
export const REDELIVER_DELIVERY = gql`
  mutation RedeliverDelivery($organizationId: Long!, $deliveryId: Long!) {
    redeliverDelivery(organizationId: $organizationId, deliveryId: $deliveryId) {${REDELIVER_RESULT_FRAGMENT}}
  }
`;

export interface RedeliverDeliveryData {
  redeliverDelivery: RedeliverResult;
}

export interface RedeliverDeliveryVars {
  organizationId: number;
  deliveryId: number;
}

/**
 * Re-push the batch's MERGED artifact to every channel that originally
 * received it. Voided channels are silently skipped server-side and named
 * in `skippedChannels`. Rejects with a GraphQL error when the batch has
 * no merged artifact, has no deliveries at all (stage-only batch), or has
 * non-terminal deliveries in flight.
 */
export const REDELIVER_BATCH = gql`
  mutation RedeliverBatch($organizationId: Long!, $batchId: Long!) {
    redeliverBatch(organizationId: $organizationId, batchId: $batchId) {${REDELIVER_RESULT_FRAGMENT}}
  }
`;

export interface RedeliverBatchData {
  redeliverBatch: RedeliverResult;
}

export interface RedeliverBatchVars {
  organizationId: number;
  batchId: number;
}

/**
 * Production retry for a `failed` batch: restores released
 * state (failed runs back to pending, or the failed merge claim cleared)
 * and re-invokes the coordinator with the `retry` trigger. Inserts no new
 * runs and no new delivery rows. Rejects superseded scheduled batches
 * with a GraphQL error naming the superseding batch.
 */
export const RETRY_DATA_EXPORT_BATCH = gql`
  mutation RetryDataExportBatch($organizationId: Long!, $batchId: Long!) {
    retryDataExportBatch(organizationId: $organizationId, batchId: $batchId) {${RESULT_FRAGMENT}}
  }
`;

export interface RetryDataExportBatchData {
  retryDataExportBatch: BackfillInvokeResult;
}

export interface RetryDataExportBatchVars {
  organizationId: number;
  batchId: number;
}
